// <ThreadGraph> — FXC-8538 (Warp).
//
// The shared entry component every surface embeds. It resolves the
// chosen renderer (by name from the registry, or a component passed
// directly), measures its container when explicit dimensions aren't
// given, and hands the renderer canonical nodes/edges plus the host's
// behaviour callbacks.
//
// The host owns data sourcing (Tauri IPC for Bobbin, HTTP for
// Thread-web), filtering (`filterGraph` from core), and actions
// (deep-links, opening an editor). This component owns only resolution,
// sizing, and delegation — so the same graph looks and behaves
// identically wherever it is embedded.

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import type {
  GraphControls,
  GraphRenderer,
  GraphRendererProps,
} from "./GraphRenderer";
import { getRenderer } from "./rendererRegistry";

export interface ThreadGraphProps
  extends Omit<GraphRendererProps, "width" | "height"> {
  /**
   * Renderer to use: a registered name (e.g. `"force"`) or a renderer
   * component directly. Defaults to `"force"`.
   */
  renderer?: string | GraphRenderer;
  /** Explicit width in px. Omit to auto-measure the container. */
  width?: number;
  /** Explicit height in px. Omit to auto-measure the container. */
  height?: number;
  /** Wrapper style — the measured container fills it. */
  style?: CSSProperties;
  className?: string;
}

const MIN_DIMENSION = 200;

export function ThreadGraph({
  renderer = "force",
  width,
  height,
  style,
  className,
  nodes,
  edges,
  ...rendererProps
}: ThreadGraphProps) {
  const Renderer = resolveRenderer(renderer);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const autoSize = width === undefined || height === undefined;
  const [measured, setMeasured] = useState<{ w: number; h: number }>({
    w: MIN_DIMENSION,
    h: MIN_DIMENSION,
  });

  // Follow the container when dimensions aren't pinned (the Bobbin
  // overlay can be dragged to a new size).
  useLayoutEffect(() => {
    if (!autoSize) return;
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setMeasured({
        w: Math.max(MIN_DIMENSION, rect.width),
        h: Math.max(MIN_DIMENSION, rect.height),
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, [autoSize]);

  const w = width ?? measured.w;
  const h = height ?? measured.h;

  useEffect(() => {
    if (!Renderer) rendererProps.onReady?.(null);
  }, [Renderer, rendererProps.onReady]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: "relative", width: "100%", height: "100%", ...style }}
    >
      {Renderer ? (
        <Renderer {...rendererProps} nodes={nodes} edges={edges} width={w} height={h} />
      ) : (
        <div style={{ padding: 8, font: "12px ui-monospace, monospace", opacity: 0.7 }}>
          unknown graph renderer: {String(renderer)}
        </div>
      )}
    </div>
  );
}

function resolveRenderer(
  renderer: string | GraphRenderer,
): GraphRenderer | undefined {
  return typeof renderer === "string" ? getRenderer(renderer) : renderer;
}

export type { GraphControls };
